import { TypedDocumentNode, gql } from '@apollo/client';

import {
  AmountWithConversion,
  Props as AmountWithConversionProps,
} from '@sorare/core/src/components/buyActions/AmountWithConversion';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { useStoreAmount } from '@sorare/core/src/hooks/useStoreAmount';

import type { PrimaryOfferPrice_primaryOffer } from './__generated__/index.graphql';

type Props = {
  primaryOffer: PrimaryOfferPrice_primaryOffer;
} & Omit<
  AmountWithConversionProps,
  'monetaryAmount' | 'actualMain' | 'actualExponent'
>;

export const PrimaryOfferPrice = ({ primaryOffer, ...props }: Props) => {
  const { toMonetaryAmount } = useMonetaryAmount();
  const { storeMain, storeExponent } = useStoreAmount({
    monetaryAmount: toMonetaryAmount(primaryOffer.price),
    googlePrice: primaryOffer.googlePrice,
    applePrice: toMonetaryAmount(primaryOffer.applePrice),
  });

  if (!primaryOffer.price) return null;

  return (
    <AmountWithConversion
      monetaryAmount={primaryOffer.price}
      actualMain={storeMain}
      actualExponent={storeExponent}
      {...props}
      hideExponent={false}
    />
  );
};

PrimaryOfferPrice.fragments = {
  primaryOffer: gql`
    fragment PrimaryOfferPrice_primaryOffer on TokenPrimaryOffer {
      id
      price {
        ...useMonetaryAmount_monetaryAmount
      }
      googlePrice(countryCode: $googlePlayStoreCountryCode)
        @include(if: $isAndroidApp)
      applePrice @include(if: $isIosApp) {
        ...useMonetaryAmount_monetaryAmount
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<PrimaryOfferPrice_primaryOffer>,
};
